import React, { useEffect, useMemo, useState } from "react";
import { Layout, message, Spin } from "antd";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import ApiService from "../../Components/apiService";
import { BASE_URL } from "../../Components/Constant";

import TimesheetHeader from "./TimesheetHeader";
import TimesheetTable from "./TimesheetTable";
import AddProjectModal from "./AddProjectModal";
import UploadCsvModal from "./UploadCSVModal";
import FloatingActions from "./FloatingActions";
import { all } from "axios";

dayjs.extend(isoWeek);
const { Content } = Layout;

const Timesheet = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [weekRef, setWeekRef] = useState(() => dayjs().startOf("isoWeek"));
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timesheetMap, setTimesheetMap] = useState(null);
  const [deletedTimesheetIds, setDeletedTimesheetIds] = useState([]);

  // Compute all days of the week
  const getWeekDates = (reference) => {
    const monday = reference.clone().startOf("isoWeek");
    return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, "day"));
  };
  
  
  
  const weekDates = useMemo(() => (weekRef ? getWeekDates(weekRef) : []), [weekRef]);
  const dateKeys = weekDates.map((d) => d.format("YYYY-MM-DD"));
  // --- INITIAL LOAD ---
  useEffect(() => {
    fetchUsersAndProjects();
  }, []);

  // --- WHEN USER OR WEEK CHANGES ---
  useEffect(() => {
    if (selectedUser && weekRef) {
      fetchUserTimesheetsProjects(selectedUser, weekRef);
    }
  }, [selectedUser, weekRef]);

  // ---------------------
  // Method 1: Fetch all data (users, projects, timesheet)
  // ---------------------
  const fetchUsersAndProjects = async () => {
    setLoading(true);
    console.log("Fetching users and projects...");
    try {
      const apiService = new ApiService(
        `${BASE_URL}/api/classes/execute`,
        { "Content-Type": "application/json" },
        "POST",
        {
          action_info: {
            class_name: "TimesheetController",
            method: "fetch_users_and_projects",
            args: null,
          },
        }
      );

      const response = await apiService.makeCall();
      console.log("Response: fetch_users_and_projects", response);

      const data = response?.result || {};
      if (!data) throw new Error("No data returned");

      setUsers(data.users || []);
      setProjects(data.projects || []);
      setSelectedUser(data.current_user?._id || null);

      if (data.timesheets_with_entries) {
        setRows(transformTimesheetData(data.timesheets_with_entries));
      }

      if (data.current_week) {
        // ensure weekRef is the MONDAY of the backend-provided date
        const backendStart = dayjs(data.current_week.start).startOf("isoWeek");
        setWeekRef(backendStart);
      }
      


    } catch (err) {
      console.error(err);
      message.error("Failed to fetch users and projects");
    }
    finally {
      setLoading(false);
    }
  };

  // ---------------------
  
  const fetchUserTimesheetsProjects = async (userId, weekRef) => {
    setLoading(true);
    try {
      const weekStart = weekRef.isoWeekday(1).format("YYYY-MM-DD");
      const weekEnd   = weekRef.isoWeekday(7).format("YYYY-MM-DD");
  
      const apiService = new ApiService(
        `${BASE_URL}/api/classes/execute`,
        { "Content-Type": "application/json" },
        "POST",
        {
          action_info: {
            class_name: "TimesheetController",
            method: "fetch_user_timesheets_with_entries",
            args: [userId, weekStart, weekEnd],
          },
        }
      );
  
      const response = await apiService.makeCall();
      const resultArray = response?.result || [];
  
      // ✅ Projects: take once + dedupe by project+assignment
      const allProjects = resultArray[0]?.projects || [];
      const uniqueProjects = Array.from(
        new Map(
          allProjects.map(p => [`${p._id}-${p.Assignment}`, p])
        ).values()
      );
  
      // ✅ Timesheet map (keyed by project+assignment)
      const timesheetMap = {};
      resultArray.forEach(t => {
        if (t.timesheet?.Project && t.timesheet?.Assignment) {
          const key = `${t.timesheet.Project._id}-${t.timesheet.Assignment}`;
          timesheetMap[key] = {
            id: t.timesheet._id,
            status: t.timesheet.Status || "Draft"
          };
        }
      });
  
      setTimesheetMap(timesheetMap);
      setProjects(uniqueProjects);
  
      // ✅ Rows only when entries exist
      if (!resultArray.length || resultArray.every(r => !r.time_entries?.length)) {
        setRows([]);
      } else {
        setRows(transformTimesheetData(resultArray, uniqueProjects));
      }
  
    } catch (err) {
      console.error("❌ Failed to fetch timesheets:", err);
      message.error("Failed to fetch timesheets");
    } finally {
      setUnsavedChanges(false);
      setLoading(false);
    }
  };
  
  
  const transformTimesheetData = (timesheetsWithEntries, allProjects = []) => {
    // timesheetsWithEntries is an array of { timesheet, time_entries, ... }
    console.log("Transforming timesheet for project:", allProjects);
    console.log("Timesheets with entries:", timesheetsWithEntries);
    return timesheetsWithEntries.map((ts) => {
      const projectId = ts?.timesheet?.Project?._id || ts?.project?._id;
      const projectName = ts?.timesheet?.Project?.Name || ts?.project?.Name || "Unnamed Project";
      const status = ts?.timesheet?.Status || "Draft";
      const timesheetId = ts.timesheet?._id;
      const assignmentId = ts?.timesheet?.Assignment?._id || allProjects.find(p => p._id === projectId)?.Assignment || null;
      console.log("Processing timesheet for project:", projectName, "Assignment ID:", assignmentId);
      const assignmentData = allProjects.find(p => p.Assignment === assignmentId) || {};

      const billing_type = assignmentData.billing_type || null;

      const entries = {};
      (ts.time_entries || []).forEach(entry => {
        const dateKey = dayjs(entry.Date).format("YYYY-MM-DD");
        // preserve _id so frontend can use it for updates
        entries[dateKey] = {
          _id: entry._id || entry._id?._id || entry._id?.to_s, // defensive
          hours: entry.Hour || 0,
          notes: entry.Note || ""
        };
      });

      return {
        timesheetId,
        projectId,
        projectName,
        assignmentId,
        billing_type,
        assignmentName: ts?.timesheet?.AssignmentName || assignmentData.AssignmentName || "",
        assignmentStart: ts?.timesheet?.AssignmentStart || assignmentData.AssignmentStart || null,
        assignmentEnd: ts?.timesheet?.AssignmentEnd || assignmentData.AssignmentEnd || null,
        status,
        entries,
        isSelectedForApproval: status === "Pending Approval",
      };

    });
  };
  
  
  
  const handleClonePreviousWeek = async () => {
    if (!selectedUser || !weekRef) return;
  
    // Check if current week already has any timesheet data
    const currentWeekStart = weekRef.isoWeekday(1).format("YYYY-MM-DD");
    const currentWeekEnd = weekRef.isoWeekday(7).format("YYYY-MM-DD");
  
    setLoading(true);
  
    try {
      // 🔹 Fetch current week’s timesheets first
      const currentWeekApi = new ApiService(
        `${BASE_URL}/api/classes/execute`,
        { "Content-Type": "application/json" },
        "POST",
        {
          action_info: {
            class_name: "TimesheetController",
            method: "fetch_user_timesheets_with_entries",
            args: [selectedUser, currentWeekStart, currentWeekEnd],
          },
        }
      );
  
      const currentResponse = await currentWeekApi.makeCall();
      const currentResult = currentResponse?.result || {};
      const currentTimesheets = currentResult.timesheets || [];

      const hasCurrentEntries = currentTimesheets.some(
        (r) => Array.isArray(r.time_entries) && r.time_entries.length > 0
      );

      //  Prevent cloning if current week already has data
      if (hasCurrentEntries || (rows && rows.length > 0)) {
        message.warning("Cannot clone — current week already has timesheet entries.");
        setLoading(false);
        return;
      }
  
      // Fetch previous week’s data
      const previousWeekRef = weekRef.subtract(1, "week");
      const weekStart = previousWeekRef.isoWeekday(1).format("YYYY-MM-DD");
      const weekEnd = previousWeekRef.isoWeekday(7).format("YYYY-MM-DD");
  
      const prevWeekApi = new ApiService(
        `${BASE_URL}/api/classes/execute`,
        { "Content-Type": "application/json" },
        "POST",
        {
          action_info: {
            class_name: "TimesheetController",
            method: "fetch_user_timesheets_with_entries",
            args: [selectedUser, weekStart, weekEnd],
          },
        }
      );
  
      const response = await prevWeekApi.makeCall();
      const resultArray = response?.result || [];
  
      
      //  Check if there are NO previous week timesheets
      if (!resultArray.length || resultArray.every((r) => !r.time_entries?.length)) {
        message.warning("No previous week timesheet found to clone.");
        return;
      }
  
      //  Transform & adjust data
      const projectsFromBackend = resultArray[0]?.projects || projects;
      const clonedRows = transformTimesheetData(resultArray, projectsFromBackend);
  
      const newWeekStart = weekRef.isoWeekday(1);
      const prevWeekStart = previousWeekRef.isoWeekday(1);
  
      const adjustedRows = clonedRows.map((row) => {
        const newEntries = {};
        Object.entries(row.entries || {}).forEach(([date, val]) => {
          const dayOffset = dayjs(date).diff(prevWeekStart, "day");
          const newDate = newWeekStart.add(dayOffset, "day").format("YYYY-MM-DD");
          // newEntries[newDate] = { ...val }; For deep clone
          newEntries[newDate] = { hours: 0, notes: "" };

        });
        return { ...row, entries: newEntries, status: "Draft", isSelectedForApproval: false };
      });
  
      setRows(adjustedRows);
      setUnsavedChanges(true);
      message.success("Previous week's data cloned. Review and save to confirm.");
    } catch (err) {
      console.error(err);
      message.error("Failed to clone previous week's data");
    } finally {
      setLoading(false);
    }
  };

  
  const handleSave = async ({ deletedIds = [], updatedRows = [] }) => {
    console.log('Saving timesheet rows:', rows);
    for (const row of rows) {
      for (const [dateKey, entry] of Object.entries(row.entries || {})) {
        // const hours = entry.hours || 0;
        // if (!hours || hours < 0) {
        //   // make sure this entry is excluded from payload
        //   continue;
        // }
            
        const entryDate = dayjs(dateKey);
        const start = row.assignmentStart ? dayjs(row.assignmentStart) : null;
        const end   = row.assignmentEnd ? dayjs(row.assignmentEnd) : null;
    
        if (start && entryDate.isBefore(start, "day")) {
          return message.error(
            `${row.projectName}: ${entryDate.format("DD MMM YYYY")} is before assignment start (${dayjs(start).format("DD MMM YYYY")})`
          );
        }
    
        if (end && entryDate.isAfter(end, "day")) {
          return message.error(
            `${row.projectName}: ${entryDate.format("DD MMM YYYY")} is after assignment end (${dayjs(end).format("DD MMM YYYY")})`
          );
        }
    
        if (!entry.notes?.trim()) {
          return message.error(
            `Notes required for ${row.projectName} on ${entryDate.format("ddd, DD MMM")}`
          );
        }
        if (!entry.hours && entry.notes?.trim()) {
          return message.error(
            `Notes not allowed without hours for ${row.projectName} on ${entryDate.format("ddd, DD MMM")}`
          );
        }
        
      }
    }
    
    try {
      setLoading(true);
  
      const weekStart = weekRef.isoWeekday(1).format("YYYY-MM-DD");
      const weekEnd = weekRef.isoWeekday(7).format("YYYY-MM-DD");
      console.log('deletedTimesheetIds to process:', deletedIds);
      // 1️⃣ Handle Deleted Timesheets FIRST (even if rows = [])
      if (deletedIds.length > 0) {
          const payload = {
          action_info: {
            class_name: "TimesheetController",
            method: "delete_multiple_timesheets",
            args: [JSON.stringify(deletedIds)]
          }
        };
  
        const api = new ApiService(
          `${BASE_URL}/api/classes/execute`,
          { "Content-Type": "application/json" },
          "POST",
          payload
        );
  
        await api.makeCall();
        message.success("Deleted selected timesheets successfully");
      }
  
      // 2️⃣ If no rows exist, stop here AFTER delete
      if (rows.length === 0) {
        message.success("Timesheet saved successfully");
        await fetchUserTimesheetsProjects(selectedUser, weekRef);
        setUnsavedChanges(false);
        return;
      }
  
      // 3️⃣ Otherwise save rows as usual
      const allPromises = updatedRows.map(async (row) => {
        const projectId = row.projectId;
        // const existingTimesheet = row.timesheetId
        // ? timesheetMap?.[row.timesheetId]
        // : null;
        const existingTimesheetId = row.timesheetId
        
        const statusToSave = row.status === 'Rejected' ? 'Draft' : row.status;
  
        const entryPayloads = Object.entries(row.entries || {})
        .filter(([_, val]) => Number(val.hours) > 0) // 🔥 skip 0-hour entries
        .map(([date, val]) => ({
          _id: val._id || null,
          data: {
            Project: projectId,
            Date: date,
            Hour: val.hours || 0,
            Note: val.notes || "",
            ResourceFor: selectedUser,
            Assignment: row.assignmentId,
          },
        }));
  
        if (existingTimesheetId) {
          const payload = {
            action_info: {
              class_name: "TimesheetController",
              method: "update_timesheet_and_entries",
              args: [existingTimesheetId, JSON.stringify(entryPayloads), statusToSave],
            },
          };
          const api = new ApiService(
            `${BASE_URL}/api/classes/execute`,
            { "Content-Type": "application/json" },
            "POST",
            payload
          );
          return api.makeCall();
        }
  
        const tsPayload = {
          object_name: "Timesheet",
          data: {
            ResourceFor: selectedUser,
            Status: statusToSave,
            Project: projectId,
            Assignment: row.assignmentId,
            WeekStartDate: weekStart,
            WeekEndDate: weekEnd,
          },
        };
  
        const combinedPayload = {
          action_info: {
            class_name: "TimesheetController",
            method: "save_timesheet_and_entries",
            args: [JSON.stringify(tsPayload), JSON.stringify(entryPayloads)],
          },
        };
  
        const api = new ApiService(
          `${BASE_URL}/api/classes/execute`,
          { "Content-Type": "application/json" },
          "POST",
          combinedPayload
        );
  
        return api.makeCall();
      });
  
      await Promise.all(allPromises);
  
      message.success("Timesheet saved successfully");
      await fetchUserTimesheetsProjects(selectedUser, weekRef);
      setUnsavedChanges(false);
  
    } catch (err) {
      console.error("Error:", err);
      message.error("Failed to save timesheet");
    } finally {
      setLoading(false);
      setDeletedTimesheetIds([]);
    }
  };
  
  

  
  // ---------------------
  //  UI Rendering
  // ---------------------
  return (
    <Layout style={{ background: "#F5F7FA", minHeight: "100vh", position: "relative" }}>
      <TimesheetHeader
        users={users}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        weekRef={weekRef}
        setWeekRef={setWeekRef}
        onClonePreviousWeek={handleClonePreviousWeek}
        timesheetMap={timesheetMap}
        setDeletedTimesheetIds={setDeletedTimesheetIds}
      />

      <Content style={{ padding: 20 }}>
      <Spin spinning={loading} size="large">

        {selectedUser && weekRef && (
          <TimesheetTable
            rows={rows}
            setRows={setRows}
            weekDates={weekDates}
            dateKeys={dateKeys}
            setUnsavedChanges={setUnsavedChanges}
            unsavedChanges={unsavedChanges}
            openAddModal={() => setAddModalVisible(true)}
            openUploadModal={() => setUploadModalVisible(true)}
            onClonePreviousWeek={handleClonePreviousWeek}
            setDeletedTimesheetIds={setDeletedTimesheetIds}
            onCancel={() => fetchUserTimesheetsProjects(selectedUser, weekRef)}
            onSave={handleSave}
          />
        )}
        </Spin>
      </Content>

      {unsavedChanges && (
        <FloatingActions
          onCancel={() => fetchUserTimesheetsProjects(selectedUser, weekRef)}
          onSave={() => handleSave({ deletedIds: deletedTimesheetIds, updatedRows: rows })}
        />
      )}

      <AddProjectModal
        visible={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        setRows={setRows}
        projects={projects}
        rows={rows}
        setUnsavedChanges={setUnsavedChanges}

      />
      <UploadCsvModal
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
        projects={projects}
        fetchUserTimesheetsProjects={fetchUserTimesheetsProjects}
        selectedUser={selectedUser}
        weekRef={weekRef}
      />
    </Layout>
  );
};

export default Timesheet;
